import { DcaAllocation, DcaStrategy, DcaTransactions } from "@/types/dca";
import axiosInstance from "./axiosInstance";
import { AxiosResponse } from 'axios';
import { DataLineChart, ParamsChart } from "@/types/common";
import { TransactionFilterParams } from '@/types/filters';
import { cleanFilterParams } from '@/core/utils/filters';
import { DcaStatus } from "@/store/dca-strategy/enum";

export class DcaService {
  private static readonly GET_LIST_DCA_STRATEGY_ENDPOINT = 'app/dca-strategies';

  private static readonly CREATE_DCA_STRATEGY_ENDPOINT = 'app/dca-strategies';
  private static readonly UPDATE_DCA_STRATEGY_ENDPOINT = 'app/dca-strategies';
  private static readonly GET_DCA_STRATEGY_ENDPOINT = 'app/dca-strategies';
  private static readonly GET_ALL_DCA_MODEL_ENDPOINT = 'app/users/binance/dca-models';
  private static readonly GET_DCA_STRATEGY_REPORT_ENDPOINT = 'app/dca-strategies';

  private static toApiAllocationBasisPoints(percent: unknown): number {
    // Input is UI percent (0..100). Output is basis points integer (0..10000).
    const n = typeof percent === 'number' ? percent : Number(percent);
    if (!Number.isFinite(n)) return 0;
    // clamp percent
    const clampedPercent = Math.min(100, Math.max(0, n));
    // round UI to 2 decimals then convert to basis points per spec: (float_2_decimals * 100).toInt()
    // Here float_2_decimals means percent with 2 decimals; multiply by 100 to get basis points.
    return Math.trunc(Number(clampedPercent.toFixed(2)) * 100);
  }

  private static normalizeStrategyForWrite(data: DcaStrategy): DcaStrategy {
    // If we are stopping the strategy, we don't need to validate the rest of the form
    if (data.dcaStatus === DcaStatus.STOPPED) {
      return data;
    }

    // Validate inputs: must have at least one coin
    const list = data.coinModelRequest ?? [];
    if (!Array.isArray(list) || list.length === 0) {
      throw new Error('DCA allocation must contain at least one coin');
    }

    // Validate each percentage is a UI percent (0..100) and finite
    const roundedPercents: number[] = [];
    for (const item of list) {
      const raw = (item as any)?.percentage;
      const num = typeof raw === 'number' ? raw : Number(raw);
      const safeNum = Number.isFinite(num) ? num : 0;
      if (safeNum < 0 || safeNum > 100) {
        // Hint if API basis points were mistakenly provided
        const hint = safeNum > 100 ? ' (UI percents expected 0..100; do not pass API basis points 0..10000)' : '';
        throw new Error(`Allocation percentage out of range for ${item?.name ?? 'coin'}: ${safeNum}${hint}`);
      }
      roundedPercents.push(Number(safeNum.toFixed(2)));
    }

    // Validate total equals exactly 100.00 after UI rounding
    const totalRounded = Number(roundedPercents.reduce((a, b) => a + b, 0).toFixed(2));
    if (totalRounded !== 100) {
      throw new Error(`DCA allocation total must be exactly 100% after rounding to 2 decimals (got ${totalRounded}%)`);
    }

    // Step 1: Convert each coin's UI percent (0..100, may have >2 decimals) into exact basis points target
    const coins = list.map((coin, index) => {
      const roundedPercent2 = Number((roundedPercents[index] ?? 0).toFixed(2));
      const exactBp = roundedPercent2 * 100; // can be fractional like 12.34% -> 1234.0 bp exactly after 2-dec rounding
      // Use the single-source helper for the floor/trunc conversion to basis points
      const floorBp = DcaService.toApiAllocationBasisPoints(roundedPercent2);
      const remainder = exactBp - floorBp;
      return { index, coin, floorBp, remainder };
    });

    // Step 2: Distribute remainders to ensure total sum equals 10000
    const totalFloors = coins.reduce((sum, c) => sum + c.floorBp, 0);
    let delta = 10000 - totalFloors; // how many 1-bp units to add (or remove if negative)

    const resultBp = coins.map(c => ({ index: c.index, bp: c.floorBp, remainder: c.remainder }));

    if (delta !== 0 && resultBp.length > 0) {
      if (delta > 0) {
        // Add 1 bp to coins with largest remainders first; tiebreaker by original index
        const order = [...resultBp].sort((a, b) => {
          if (b.remainder !== a.remainder) return b.remainder - a.remainder;
          return a.index - b.index;
        });
        for (let i = 0; i < order.length && delta > 0; i++) {
          order[i].bp += 1;
          delta--;
          if (i === order.length - 1 && delta > 0) i = -1; // wrap around if still remaining
        }
      } else {
        // Remove 1 bp from coins with smallest remainders (or largest negatives) first
        delta = -delta;
        const order = [...resultBp].sort((a, b) => {
          if (a.remainder !== b.remainder) return a.remainder - b.remainder;
          return a.index - b.index;
        });
        for (let i = 0; i < order.length && delta > 0; i++) {
          if (order[i].bp > 0) { // don't go negative
            order[i].bp -= 1;
            delta--;
          }
          if (i === order.length - 1 && delta > 0) i = -1; // wrap if needed
        }
      }
    }

    // Step 3: Map back to original order and clamp each bp to [0, 10000]
    const bpByIndex = new Map(resultBp.map(r => [r.index, Math.min(10000, Math.max(0, r.bp))]));
    const coinModelRequest = coins.map(c => ({
      ...c.coin,
      // Rename field to percentage remains same field name, but now basis points integer as API expects
      percentage: bpByIndex.get(c.index) ?? 0,
    }));

    return { ...data, coinModelRequest };
  }

  public static async getListDcaStrategy(): Promise<DcaStrategy[]> {
    const res: AxiosResponse<DcaStrategy[]> = await axiosInstance.get(DcaService.GET_LIST_DCA_STRATEGY_ENDPOINT);
    return res.data;
  }

  public static async createDCAStrategy(data: DcaStrategy): Promise<DcaStrategy> {
    const payload = DcaService.normalizeStrategyForWrite(data);
    const res: AxiosResponse<DcaStrategy> = await axiosInstance.post(DcaService.CREATE_DCA_STRATEGY_ENDPOINT, payload);
    return res.data;
  }

  public static async updateDCAStrategy(data: DcaStrategy): Promise<DcaStrategy> {
    const payload = DcaService.normalizeStrategyForWrite(data);
    const res: AxiosResponse<DcaStrategy> = await axiosInstance.patch(`${DcaService.UPDATE_DCA_STRATEGY_ENDPOINT}/${data.id}`, payload);
    return res.data;
  }

  public static async getDCAStrategy(id: string): Promise<DcaStrategy> {
    const res: AxiosResponse<DcaStrategy> = await axiosInstance.get(`${DcaService.GET_DCA_STRATEGY_ENDPOINT}/${id}`);
    return res.data;
  }

  public static async getAllDCAModel(): Promise<DcaStrategy[]> {
    const res: AxiosResponse<DcaStrategy[]> = await axiosInstance.get(DcaService.GET_ALL_DCA_MODEL_ENDPOINT);
    return res.data;
  }

  public static async getDcaAllocations(id: string): Promise<DcaAllocation[]> {
    const res: AxiosResponse<DcaAllocation[]> = await axiosInstance.get(`${DcaService.GET_DCA_STRATEGY_ENDPOINT}/${id}/allocations`);
    return res.data;
  }

  public static async getDcaTransactions(params: { id: string, offset?: number, limit?: number, filters?: TransactionFilterParams }): Promise<DcaTransactions> {
    const { id, offset = 0, limit = 10, filters } = params;
    const res: AxiosResponse<DcaTransactions> = await axiosInstance.get(`${DcaService.GET_DCA_STRATEGY_ENDPOINT}/${id}/transactions`, {
      params: {
        offset,
        limit,
        ...cleanFilterParams(filters),
      }
    });
    return res.data;
  }

  public static async getDcaStrategyReport(params: ParamsChart): Promise<DataLineChart> {
    const { id, period } = params;
    const res: AxiosResponse<DataLineChart> = await axiosInstance.get(`${DcaService.GET_DCA_STRATEGY_REPORT_ENDPOINT}/${id}/report`, {
      params: {
        period
      }
    });
    return res.data;
  }
}
